import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import LandingPage from './pages/LandingPage';
import PublicApply from './pages/PublicApply';
import Careers from './pages/Careers';
import CareerJobDetail from './pages/CareerJobDetail';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Unsubscribe from './pages/Unsubscribe';
import { Routes, Route } from 'react-router-dom';
import './styles/globals.css';
import './styles/ui.css';
// After the component CSS so Tailwind utilities win ties. mobile.css is
// imported further down and still overrides layout at small widths.
import './styles/tailwind.css';
import { registerServiceWorker } from './registerSW';
import { initTheme } from './lib/theme';

// Phase 2 — stamp the resolved theme (saved choice ?? OS preference) onto
// <html> BEFORE first paint so there's no light-mode flash. Runs for both
// the app and the public marketing pages (they share this entry).
initTheme();

// Installable PWA + offline shell (production only; no-op in dev).
registerServiceWorker();

// Capture ?demo=1 BEFORE the MSAL flow — an auth redirect strips the query
// string, and the DemoBanner (which normally records the flag) only mounts
// after auth. sessionStorage keeps the flag per-tab.
try {
  if (new URLSearchParams(window.location.search).get('demo') === '1') {
    sessionStorage.setItem('fns_demo', '1');
  }
} catch { /* sessionStorage unavailable (private mode edge) — banner still reads the param directly */ }
import './styles/mobile.css';
import { setTokenGetter, PUBLIC_API_BASE } from './lib/api';
import { installErrorReporting } from './lib/errorReporting';
import { ToastProvider } from './components/ToastHost';
import { ConfirmProvider } from './components/ConfirmHost';
import { MsalProvider, msalInstance, useAuth } from './lib/auth';
import { EventType } from '@azure/msal-browser';
import { PermissionsProvider } from './contexts/PermissionsContext';
import { IS_DEMO_BUILD, demoAccessCode, demoBearerToken, setDemoAccessCode } from './lib/demoMode';
// [split W1-B] Platform identity (control plane) — the demo gate is a PLATFORM
// surface, so its name comes from here, not a hardcoded platform-name literal.
import { PLATFORM_NAME } from './lib/platformIdentity';
// "/" doubles as MSAL's redirect URI — see the note at the isPublic check.
import { shouldRenderLanding } from './lib/authRedirectResponse';

// One-shot — attaches window.onerror + unhandledrejection listeners that
// POST client-side errors to /api/v1/error-log/client for admin triage.
installErrorReporting();

// ─── MSAL initialisation ───────────────────────────────────────────────
// MSAL needs a one-time initialize() call before React renders — without
// it, acquireTokenSilent throws "not yet initialized". We also handle the
// LOGIN_SUCCESS event to set the active account, which is what React's
// useAccount() reads.
//
// CRITICAL: this promise MUST have a .catch() AND a timeout. If
// initialize() rejects (bad tenant ID, popup blocker, third-party-cookie
// block, transient network), without a catch nothing renders — React
// never mounts — and the user sees a permanent white screen with no
// error message and no way to recover. If initialize() *hangs* (network
// stall, slow DNS, Azure outage), neither .then nor .catch ever fires,
// which produced the same white-screen symptom for a different reason.
// The Promise.race below caps the wait at 12s and surfaces a recovery
// card via renderInitFallback so the user always lands on something
// actionable rather than a blank page.
const MSAL_INIT_TIMEOUT_MS = 12_000;

// Lazily constructed (only on the real-auth path) so the demo build never
// spawns a floating 12s rejection with nothing racing against it.
const makeInitTimeout = () => new Promise<never>((_resolve, reject) => {
  setTimeout(
    () => reject(new Error(`MSAL initialize() exceeded ${MSAL_INIT_TIMEOUT_MS}ms — likely a network or Azure AD issue.`)),
    MSAL_INIT_TIMEOUT_MS,
  );
});

// Public, no-auth pages render standalone — no MSAL, no providers, no auth
// wall. The careers job board (/careers) and apply form (/apply) are public
// marketing pages; a visitor must never be asked to sign in or load the
// internal app.
//
// "/" is the exception, because it is BOTH the marketing landing page and
// MSAL's redirect URI (redirectUri defaults to window.location.origin). It
// only gets the standalone treatment when this page load is genuinely a
// visitor arriving — not a sign-in coming back from Microsoft with an
// authorization code, and not someone who is already signed in. Rendering the
// landing page in those cases threw the code away and bounced the user
// straight back to the marketing page, unable to sign in at all.
const path = window.location.pathname;
const isPublicPath = path === '/apply' || path === '/careers' || path.startsWith('/careers/')
  || path === '/privacy' || path === '/unsubscribe';
const isPublic = isPublicPath || (path === '/' && shouldRenderLanding());
if (isPublic) {
  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/apply" element={<PublicApply />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="/careers/:id" element={<CareerJobDetail />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/unsubscribe" element={<Unsubscribe />} />
        </Routes>
      </BrowserRouter>
    </React.StrictMode>
  );
} else if (IS_DEMO_BUILD) {
  // Demo deployment: no Microsoft sign-in. Capture ?code= from the share
  // link, verify it against the demo backend, then boot straight into the
  // app as the Demo Presenter. A wrong/missing code gets a friendly access
  // prompt instead of an app full of 401s. MSAL still initializes (against
  // its placeholder config — no network) so MsalProvider/useMsal consumers
  // mount cleanly with zero accounts.
  demoAccessCode(); // pull ?code= into sessionStorage before anything renders
  msalInstance.initialize().catch(() => { /* placeholder config — non-fatal */ }).finally(() => {
    void (async () => {
      try {
        const res = await fetch(`${PUBLIC_API_BASE}/admin/whoami`, {
          headers: { Authorization: `Bearer ${demoBearerToken()}` },
        });
        if (res.status === 401 || res.status === 403) { renderDemoCodeGate(); return; }
      } catch { /* backend unreachable — boot anyway; errors surface in-app */ }
      bootstrap();
    })();
  });
} else {
Promise.race([msalInstance.initialize(), makeInitTimeout()]).then(() => {
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0 && !msalInstance.getActiveAccount()) {
    msalInstance.setActiveAccount(accounts[0]);
  }
  msalInstance.addEventCallback((event) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
      const payload = event.payload as { account?: import('@azure/msal-browser').AccountInfo };
      if (payload.account) msalInstance.setActiveAccount(payload.account);
    }
  });

  bootstrap();
}).catch((err: unknown) => {
  renderInitFallback(err);
});
}

// Hand-rolled fallback rendered directly into #root before any React
// provider is mounted. Plain DOM, no React, no router — just enough
// for the user to see what went wrong and reload. Inline styles only
// so we don't need our CSS bundle to have parsed yet.
function renderInitFallback(err: unknown): void {
  const root = document.getElementById('root');
  if (!root) return;
  const message = err instanceof Error ? err.message : String(err);
  // Best-effort error report; if this also fails, swallow — the user
  // is already in a fallback path.
  try {
    void fetch(`${PUBLIC_API_BASE}/error-log/client`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        kind: 'msal_init_failure',
        message,
        stack: err instanceof Error ? err.stack : null,
        userAgent: navigator.userAgent,
        href: window.location.href,
      }),
      keepalive: true,
    }).catch(() => { /* fallback path — telemetry is best-effort */ });
  } catch { /* same */ }
  root.innerHTML = `
    <div style="min-height:100vh;display:flex;align-items:center;justify-content:center;background:#f8fafc;font-family:system-ui,-apple-system,'Segoe UI',Roboto,sans-serif;padding:24px">
      <div style="max-width:480px;background:#fff;border:1px solid #fecaca;border-radius:14px;padding:28px 32px;box-shadow:0 12px 32px rgba(15,23,42,0.08)">
        <div style="font-size:14px;font-weight:700;color:#dc2626;letter-spacing:0.4px;text-transform:uppercase;margin-bottom:8px">Sign-in unavailable</div>
        <h1 style="margin:0 0 12px;font-size:20px;font-weight:700;color:#111827">We couldn't reach Microsoft sign-in.</h1>
        <p style="margin:0 0 16px;font-size:14px;color:#475569;line-height:1.55">
          This usually means a popup blocker, third-party cookie block, or a transient network issue. Reloading the page will retry.
        </p>
        <div style="background:#fef2f2;border:1px solid #fee2e2;border-radius:8px;padding:10px 12px;font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:12px;color:#991b1b;margin-bottom:18px;word-break:break-word">${escapeHtml(message)}</div>
        <button id="fns-reload-btn" type="button"
          style="background:#1565c0;color:#fff;border:0;border-radius:8px;padding:10px 18px;font-weight:600;font-size:14px;cursor:pointer">
          Reload page
        </button>
      </div>
    </div>`;
  const btn = document.getElementById('fns-reload-btn');
  if (btn) btn.addEventListener('click', () => { window.location.reload(); });
}

// Demo access-code prompt — plain DOM (like renderInitFallback) so it works
// before any React provider mounts. Shown when the demo backend rejects the
// visitor's code; entering the right code stores it and boots the app.
function renderDemoCodeGate(): void {
  const root = document.getElementById('root');
  if (!root) return;
  root.innerHTML = `
    <div style="min-height:100vh;display:flex;align-items:center;justify-content:center;background:#0f172a;font-family:system-ui,-apple-system,'Segoe UI',Roboto,sans-serif;padding:24px">
      <div style="max-width:420px;width:100%;background:#fff;border-radius:16px;padding:32px;box-shadow:0 24px 60px rgba(0,0,0,0.35);text-align:center">
        <div style="font-size:34px;margin-bottom:10px">🎬</div>
        <h1 style="margin:0 0 6px;font-size:20px;font-weight:800;color:#0f172a">${escapeHtml(PLATFORM_NAME)} — Live Demo</h1>
        <p style="margin:0 0 20px;font-size:13px;color:#64748b;line-height:1.6">
          This is a private demo environment with sample data. Enter the access code you were given to continue.
        </p>
        <input id="demo-code-input" type="text" placeholder="Access code" autocomplete="off"
          style="width:100%;box-sizing:border-box;padding:12px 14px;border:1px solid #cbd5e1;border-radius:10px;font-size:15px;text-align:center;letter-spacing:2px;margin-bottom:12px" />
        <button id="demo-code-btn" type="button"
          style="width:100%;background:#0f172a;color:#fff;border:0;border-radius:10px;padding:12px;font-weight:700;font-size:14px;cursor:pointer">
          Enter Demo
        </button>
        <div id="demo-code-err" style="display:none;margin-top:10px;font-size:12px;color:#dc2626">That code didn't work — please check it and try again.</div>
      </div>
    </div>`;
  const input = document.getElementById('demo-code-input') as HTMLInputElement | null;
  const btn = document.getElementById('demo-code-btn');
  const err = document.getElementById('demo-code-err');
  const submit = async () => {
    const code = input?.value.trim() ?? '';
    if (!code) return;
    setDemoAccessCode(code);
    try {
      const res = await fetch(`${PUBLIC_API_BASE}/admin/whoami`, {
        headers: { Authorization: `Bearer ${demoBearerToken()}` },
      });
      if (res.status === 401 || res.status === 403) {
        if (err) err.style.display = 'block';
        return;
      }
    } catch { /* backend hiccup — proceed and let the app surface it */ }
    window.location.replace('/');
  };
  if (btn) btn.addEventListener('click', () => { void submit(); });
  if (input) {
    input.focus();
    input.addEventListener('keydown', (e) => { if (e.key === 'Enter') void submit(); });
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => (
    c === '&' ? '&amp;' :
    c === '<' ? '&lt;' :
    c === '>' ? '&gt;' :
    c === '"' ? '&quot;' : '&#39;'
  ));
}

function bootstrap() {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 1000 * 60 * 2, // 2 minutes
        retry: 1,
      },
    },
  });

  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <MsalProvider instance={msalInstance}>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <ToastProvider>
              <ConfirmProvider>
                <PermissionsProvider>
                  <AppWithAuth />
                </PermissionsProvider>
              </ConfirmProvider>
            </ToastProvider>
          </BrowserRouter>
        </QueryClientProvider>
      </MsalProvider>
    </React.StrictMode>
  );
}

// Inner component that has access to our MSAL-backed auth hook. Wires
// the token getter for our axios instance so every API call picks up
// the current Azure access token.
function AppWithAuth() {
  const { getToken } = useAuth();
  React.useEffect(() => {
    setTokenGetter((opts) => getToken(opts));
  }, [getToken]);

  return <App />;
}
